import { useState, useEffect } from 'react'
import { submitVolunteer, fetchVolunteerProfiles } from '../services/api'

const Volunteer = () => {
  // Volunteer Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalMounted, setIsModalMounted] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    area_of_interest: 'Feeding & Sanctuary Care',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Carousel Navigation State
  const [startIndex, setStartIndex] = useState(0)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)

  // Mouse Drag / Touch Swipe State
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  // Modal Animation Lifecycle
  useEffect(() => {
    if (isModalOpen) {
      setIsModalMounted(true)
      const t = setTimeout(() => setIsModalVisible(true), 20)
      return () => clearTimeout(t)
    } else {
      setIsModalVisible(false)
      const t = setTimeout(() => setIsModalMounted(false), 350)
      return () => clearTimeout(t)
    }
  }, [isModalOpen])

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isModalOpen])

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleStartCloseModal()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isModalOpen])

  const handleStartCloseModal = () => {
    setIsModalVisible(false)
    setTimeout(() => {
      setIsModalOpen(false)
      setSubmitSuccess(false)
      setSubmitError('')
    }, 320)
  }

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    
    // Strict Input Validations
    const trimmedName = formData.name.trim()
    const trimmedPhone = formData.phone.trim()
    const trimmedEmail = formData.email.trim()

    if (!trimmedName || !trimmedPhone || !trimmedEmail) {
      setSubmitError('Please fill in all required fields.')
      return
    }

    if (/^\d+$/.test(trimmedName) || trimmedName.length < 2) {
      setSubmitError('Full Name cannot consist of only numbers. Please enter a valid name.')
      return
    }

    const cleanDigits = trimmedPhone.replace(/\D/g, '')
    if (cleanDigits.length < 10) {
      setSubmitError('Phone number cannot contain alphabets. Please enter a valid 10-digit phone number.')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setSubmitError('Please enter a valid email address (e.g. name@gmail.com).')
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    const payload = {
      ...formData,
      name: trimmedName,
      phone: trimmedPhone,
      email: trimmedEmail
    }

    const result = await submitVolunteer(payload)
    setIsSubmitting(false)

    if (result && result.success) {
      setSubmitSuccess(true)
      setFormData({
        name: '',
        email: '',
        phone: '',
        area_of_interest: 'Feeding & Sanctuary Care',
        message: ''
      })
    } else {
      setSubmitError(result?.message || 'Failed to submit application. Please try again.')
    }
  }


  const DEFAULT_VOLUNTEERS = [
    {
      id: 1,
      name: "Isaac & Linh",
      text: "The team at CAWT was very friendly and caring toward us and the animals during our shelter stay.",
      meta: "BY ISAAC AND LINH | JULY 2024",
      image_url: "/volunteer/v2.png"
    },
    {
      id: 2,
      name: "Sai Santhosh",
      text: "Hello all, I am very glad to share my rescue experience at CAWT during the past months.",
      meta: "BY SAI SANTHOSH | JUNE 2024",
      image_url: "/volunteer/v1.png"
    },
    {
      id: 3,
      name: "Georgia",
      text: "CAWT welcomed us with open arms despite our lack of experience, empowering us to care for street dogs.",
      meta: "BY GEORGIA | JUNE 2024",
      image_url: "/volunteer/v3.png"
    }
  ]

  const [volunteers, setVolunteers] = useState(DEFAULT_VOLUNTEERS)
  const [selectedHero, setSelectedHero] = useState(null)
  const [isHeroModalOpen, setIsHeroModalOpen] = useState(false)
  const [isHeroModalMounted, setIsHeroModalMounted] = useState(false)

  const handleOpenHero = (hero) => {
    setSelectedHero(hero)
    setIsHeroModalMounted(true)
    requestAnimationFrame(() => setIsHeroModalOpen(true))
  }

  const handleCloseHero = () => {
    setIsHeroModalOpen(false)
    setTimeout(() => {
      setIsHeroModalMounted(false)
      setSelectedHero(null)
    }, 300)
  }

  useEffect(() => {

    fetchVolunteerProfiles().then((data) => {
      if (Array.isArray(data)) {
        // Filter out duplicate profiles by name/id
        const uniqueData = Array.from(
          new Map(data.map((item) => [item.name || item.id, item])).values()
        )
        setVolunteers(uniqueData)
      }
    })
  }, [])


  // Responsive items count
  const isMobile = windowWidth < 640
  const isSm = windowWidth >= 640 && windowWidth < 768
  const isMd = windowWidth >= 768 && windowWidth < 1024
  const isDesktop = windowWidth >= 1024

  const visibleCards = isMobile ? 1 : isSm ? 2 : isMd ? 3 : 4
  const hasCarousel = volunteers.length > visibleCards
  const maxStartIndex = Math.max(0, volunteers.length - visibleCards)

  const handleNext = () => {
    setStartIndex((prev) => (prev >= maxStartIndex ? 0 : prev + 1))
  }

  const handlePrev = () => {
    setStartIndex((prev) => (prev <= 0 ? maxStartIndex : prev - 1))
  }

  // Drag Handlers
  const handleDragStart = (clientX) => {
    if (!hasCarousel) return
    setIsDragging(true)
    setStartX(clientX)
    setDragOffset(0)
  }

  const handleDragMove = (clientX) => {
    if (!isDragging) return
    const diff = clientX - startX
    setDragOffset(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = 50
    if (dragOffset < -threshold) {
      handleNext()
    } else if (dragOffset > threshold) {
      handlePrev()
    }
    setDragOffset(0)
  }

  return (
    <section
      id="volunteer"
      className="bg-[#FFF9F4] py-8 sm:py-12 lg:py-14 scroll-mt-24 transition-colors w-full overflow-hidden select-none"
    >
      <div className="w-full max-w-8xl mx-auto flex flex-col items-center">
        
        {/* Header Section with Navigation Controls */}
        <div className="w-full mb-6 sm:mb-8 flex flex-col md:flex-row items-start md:items-end justify-between gap-4 px-4 sm:px-6 lg:px-8 2xl:px-12 text-left">
          <div>
            <span className="px-3.5 py-1.5 bg-brand-orange-50 border border-brand-orange-100 text-brand-orange-700 font-bold text-xs uppercase rounded-full tracking-widest inline-block mb-2">
              VOLUNTEER LIST
            </span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-800">
              Meet Our Community Heroes
            </h2>
            <p className="text-slate-500 mt-2 text-sm sm:text-base max-w-xl">
              Every volunteer helps rescue, protect, and give abandoned animals a second chance at life.
            </p>
          </div>

          {/* Action Buttons & Sliding Carousel Controls */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Carousel Arrow Controls (Visible on desktop) */}
            {hasCarousel && (
              <div className="hidden md:flex items-center gap-2.5">
                <button
                  onClick={handlePrev}
                  className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-brand-orange-700 hover:bg-brand-orange-850 text-white transition-all shadow-md border border-brand-orange-800 cursor-pointer focus:outline-none active:scale-95"
                  aria-label="Previous volunteer profile"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                  </svg>
                </button>
                <button
                  onClick={handleNext}
                  className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-brand-orange-700 hover:bg-brand-orange-850 text-white transition-all shadow-md border border-brand-orange-800 cursor-pointer focus:outline-none active:scale-95"
                  aria-label="Next volunteer profile"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </svg>
                </button>
              </div>
            )}

            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 bg-brand-orange-700 hover:bg-brand-orange-850 text-white font-semibold rounded-full text-xs sm:text-sm tracking-wider uppercase shadow-md hover:shadow-lg hover:scale-105 transition-all cursor-pointer inline-flex items-center gap-2 shrink-0 whitespace-nowrap"
            >
              <span>Become a Volunteer</span>
              <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Unified Single-Line Horizontal Carousel Track (Desktop & Mobile) */}
        <div 
          className="w-full overflow-hidden px-4 sm:px-6 lg:px-8 2xl:px-12 py-1 sm:py-2 cursor-grab active:cursor-grabbing touch-pan-y"
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseMove={(e) => handleDragMove(e.clientX)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
          onTouchEnd={handleDragEnd}
        >
          <div
            className={`flex flex-nowrap gap-3.5 sm:gap-5 lg:gap-5 ${isDragging ? 'transition-none' : 'transition-transform duration-500 ease-in-out'}`}
            style={{
              transform: `translateX(calc(-${startIndex} * (${
                isMobile ? '84%' : isSm ? '50%' : isMd ? '33.333%' : '25%'
              } + ${isMobile ? '0.875rem' : '1.25rem'}) + ${dragOffset}px))`
            }}
          >
            {volunteers.map((vol, idx) => (
              <div
                key={vol.id || vol.name || idx}
                onClick={() => handleOpenHero(vol)}
                className="w-[84%] sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-0.85rem)] lg:w-[calc(25%-0.95rem)] 2xl:w-[calc(25%-1rem)] shrink-0 bg-white rounded-tl-[32px] rounded-br-[32px] sm:rounded-tl-[44px] sm:rounded-br-[44px] 2xl:rounded-tl-[52px] 2xl:rounded-br-[52px] rounded-tr-none rounded-bl-none p-3.5 sm:p-4 lg:p-4.5 border border-slate-100/90 shadow-xs hover:shadow-md flex flex-col justify-between text-left transition-all duration-300 hover:-translate-y-1 group relative overflow-hidden cursor-pointer"
              >
                {/* Header Image - Curved frame matching adoption stories */}
                <div className="h-38 xs:h-42 sm:h-46 md:h-48 lg:h-48 2xl:h-54 w-full rounded-tl-[24px] rounded-br-[24px] sm:rounded-tl-[32px] sm:rounded-br-[32px] 2xl:rounded-tl-[40px] 2xl:rounded-br-[40px] rounded-tr-none rounded-bl-none overflow-hidden shrink-0 relative bg-slate-100 mb-3 shrink-0">
                  <img
                    src={vol.image_url || vol.image || '/volunteer/v1.png'}
                    alt={vol.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/volunteer/v1.png'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/15 to-transparent" />
                </div>

                {/* Content Box */}
                <div className="flex flex-col justify-between flex-grow text-left px-0.5 space-y-2">
                  <div className="space-y-1 sm:space-y-1.5">
                    <h3 className="font-display font-black text-slate-800 text-base sm:text-lg 2xl:text-xl leading-tight">
                      {vol.name}
                    </h3>
                    <p className="text-xs sm:text-xs md:text-sm 2xl:text-base text-slate-500 font-medium leading-snug sm:leading-relaxed line-clamp-3">
                      "{vol.text}"
                    </p>
                  </div>

                  <div className="w-full pt-1.5 sm:pt-2 flex items-center justify-between">
                    <span className="block text-[9px] xs:text-[10px] 2xl:text-xs uppercase tracking-widest text-slate-400 font-bold">
                      {vol.meta}
                    </span>
                    <span className="text-[11px] font-extrabold text-brand-orange-700 group-hover:translate-x-1 transition-transform">
                      Read &rarr;
                    </span>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>


        {/* Mobile controls row (Bottom Right - Separate Dark Maroon Circular Buttons) */}

        {hasCarousel && (
          <div className="flex md:hidden justify-end items-center gap-2.5 mt-3 sm:mt-4 px-4 sm:px-6 w-full">
            <button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-orange-700 active:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
              aria-label="Previous volunteer"
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-orange-700 active:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
              aria-label="Next volunteer"
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
          </div>
        )}
      </div>


      {/* Volunteer Application Modal (Bottom-Docked Sliding) */}
      {(isModalMounted || isModalOpen) && (
        <>
          {/* Backdrop overlay */}
          <div
            className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-350 ease-in-out ${
              isModalVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleStartCloseModal}
          />

          {/* Slide-Up / Slide-Down Container */}
          <div
            className={`fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-transform duration-350 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
              isModalVisible ? 'translate-y-0' : 'translate-y-full'
            }`}
            data-lenis-prevent
          >
            <div
              className="bg-white rounded-none w-full max-w-4xl lg:max-w-5xl 2xl:max-w-6xl shadow-2xl border-t-2 border-x border-slate-300 overflow-hidden flex flex-col max-h-[88vh] text-left pointer-events-auto mb-0"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#FAF6F2] border-b border-orange-200/80 px-5 py-4 sm:px-8 sm:py-5 flex items-center justify-between shrink-0 rounded-none">
                <div className="space-y-0.5">
                  <h3 className="text-xl sm:text-2xl font-display font-bold text-slate-900 leading-snug">
                    Become a Volunteer
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 font-medium">
                    Join our passionate network of animal rescuers, caretakers, and feeders
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleStartCloseModal}
                  className="w-9 h-9 bg-slate-200/80 hover:bg-slate-300 text-slate-700 hover:text-slate-950 flex items-center justify-center font-bold text-base transition-all cursor-pointer rounded-none border border-slate-300"
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 sm:p-8 overflow-y-auto flex-1 bg-white">
                {submitSuccess ? (
                  <div className="py-8 text-center space-y-4 max-w-lg mx-auto">
                    <div className="w-14 h-14 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-300 rounded-none">
                      ✓
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-2xl font-bold text-slate-900">Application Submitted!</h4>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                        Thank you for stepping up to help. Our shelter team will review your application and reach out to you via WhatsApp or Phone call.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4 text-xs sm:text-sm">
                    {submitError && (
                      <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-none">
                        {submitError}
                      </div>
                    )}

                    {/* Row 1: Name, Email, Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-bold text-slate-800">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Rahul Sharma"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-bold text-slate-800">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="you@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                        />
                      </div>

                      <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                        <label className="block text-xs font-bold text-slate-800">
                          Phone / WhatsApp <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          required
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+\-\s()]/g, '') })}

                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium font-mono rounded-none"
                        />
                      </div>
                    </div>

                    {/* Row 2: Area of Interest */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-800">
                        Primary Area of Interest <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.area_of_interest}
                        onChange={(e) => setFormData({ ...formData, area_of_interest: e.target.value })}
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium cursor-pointer rounded-none"
                      >
                        <option value="Feeding & Sanctuary Care">Feeding &amp; Sanctuary Care</option>
                        <option value="Rescue & Transport Assistance">Rescue &amp; Transport Assistance</option>
                        <option value="Vaccination & Health Camps">Vaccination &amp; Health Camps</option>
                        <option value="Photography & Social Media">Photography &amp; Social Media</option>
                        <option value="Foster Care / Adoption Coordination">Foster Care / Adoption Coordination</option>
                      </select>
                    </div>

                    {/* Row 3: Message / Availability */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-800">
                        Availability &amp; Prior Animal Experience
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Tell us a little about your weekly availability or prior experience with animal welfare..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-3.5 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium resize-none rounded-none"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end pt-3 border-t border-slate-200">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto px-8 py-2.5 bg-brand-orange-600 hover:bg-brand-orange-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 rounded-none"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent animate-spin" />
                            <span>Submitting Application...</span>
                          </>
                        ) : (
                          <span>Submit Volunteer Application</span>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Community Hero Detail Slide-Over Drawer */}

      {(isHeroModalMounted || isHeroModalOpen) && selectedHero && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-300 z-50 ${
              isHeroModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleCloseHero}
          />

          {/* Slide-Over Drawer Container (Right-docked) */}
          <div
            data-lenis-prevent
            className={`fixed top-0 right-0 h-full w-full sm:w-[480px] lg:w-[520px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out transform border-l border-slate-200 ${
              isHeroModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-brand-orange-700 block">
                  COMMUNITY HERO STORY
                </span>
                <h3 className="text-xl sm:text-2xl font-bold font-display text-slate-900 leading-tight">
                  {selectedHero.name}
                </h3>
              </div>

              <button
                onClick={handleCloseHero}
                className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-all focus:outline-none cursor-pointer shrink-0"
                aria-label="Close story details"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable Story Body */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 text-left">
              {/* Full Image */}
              <div className="relative w-full h-56 sm:h-64 rounded-2xl overflow-hidden bg-slate-100 border border-slate-100 shadow-sm">
                <img
                  src={selectedHero.image_url || selectedHero.image || '/volunteer/v1.png'}
                  alt={selectedHero.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = '/volunteer/v1.png'; }}
                />
                {selectedHero.meta && (
                  <span className="absolute bottom-3 left-3 px-3 py-1 bg-white/95 text-slate-800 text-xs font-black uppercase tracking-wider rounded-lg shadow-sm border border-white/80">
                    {selectedHero.meta}
                  </span>
                )}
              </div>

              {/* Story Quote / Testimonial Box */}
              <div className="p-4 rounded-2xl bg-brand-orange-50/60 border border-brand-orange-100/80 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-brand-orange-700 block">
                  Volunteer Testimonial &amp; Rescue Experience
                </span>
                <p className="text-sm sm:text-base font-semibold text-slate-800 italic leading-relaxed">
                  "{selectedHero.text}"
                </p>
              </div>

              {/* Additional Details */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-700">
                  Community Impact &amp; Role
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
                  {selectedHero.name} has been actively involved in animal rescue, feeding drives, medical care, and helping stray animals transition to safe environments with Companion Animal Welfare Trust (CAWT).
                </p>
              </div>
            </div>

            {/* Footer Action */}
            <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50/80 shrink-0">
              <button
                onClick={() => {
                  handleCloseHero()
                  setTimeout(() => setIsModalOpen(true), 350)
                }}
                className="w-full py-3 bg-brand-orange-700 hover:bg-brand-orange-850 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <span>Join As A Volunteer</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}

    </section>

  )
}

// Helper to calculate exact pixel gap between cards for transform
const gapSpacing = (visibleCards) => {
  if (visibleCards === 1) return '1.5rem'
  if (visibleCards === 2) return '0.75rem'
  if (visibleCards === 4) return '1.5rem'
  return '1.333rem'
}

export default Volunteer
